/*
 * ===============================================================================================================
 *                                Copyright 2022, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LuiModal, LuiSelect } from '@jda/lui-common-component-library-mui5';
import { ArrowTrendDownFill } from '@jda/lui-common-icon-library-mui5';
import { Box, Grid, Divider, Button, TextField, Checkbox, FormControlLabel, FormHelperText, FormControl } from '@mui/material';
import PhoneInput, { type Value } from 'react-phone-number-input';
import { useStyles } from './styles';
import createFormValueGetter from './formValueGetter';
import FormDialog from '../../shared/Dialog/FormDialog';
import DisplayContent from '../../shared/DisplayContent';
import { getUserDetailsById } from '../../store/user/users';
import { AlertDialog } from '../../shared/Dialog/AlertDialog';
import { PREFERRED_MFA_LIST, NATIVE_IDP_TYPE } from '../UserManagement/constant';
import { compareByProperty } from '../../utility/commonUtility';
import interpolateString from '../../utility/stringInterpolation';
import UserPasswordDisplay from '../UserManagement/UserPasswordDisplay';
import getTextFieldHelperProps from '../UserManagement/textFieldHelperPropGetter';
import { KEY_NAMES, USER_FORM_FIELDS } from '../UserManagement/constant';
import { INativeUserDetailsProps } from '../../models/Users/UserContracts';
import { LABELS, MESSAGES } from './constant';
import { hideSnackbar, setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import { selectUserFormData } from '../../store/user/saveUser/saveUserSelector';
import { updateNativeUserDetails, resetUserPassword, signInBlockEnable, resetUserMFA, resendInvite } from '../../services/user';
import { getAllIdps, getSelectedItemDetails } from '../../store/user/userSelector';
import { updateUserFormDataField } from '../../store/user/saveUser/saveUserActionCreator';
import { selectAppLocation } from '../../store/appLocation/appLocationSelector';
import { getIdpById } from '../../services/setting';
import ResetPassword from './ResetPassword';

const NativeUserDetails = ({ userName, id, handleCopyClick }: INativeUserDetailsProps) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const selectGetAllIdps = useSelector(getAllIdps);
  const userFormValues = useSelector(selectUserFormData);
  const selectedUserDetails = useSelector(getSelectedItemDetails);
  const appLocation = useSelector(selectAppLocation);

  const idpList = useMemo(
    () => {
      const idps = [...selectGetAllIdps].sort(compareByProperty());

      return idps.map(idp => ({ ...idp, key: idp.id, displayName: idp.name }));
    },
    [selectGetAllIdps]
  );

  const formDefaultValues = {
    firstName: '',
    lastName: '',
    displayName: '',
    isAccountEnabled: true,
  };

  const formErrorDefaultValues = {
    error: false,
    errorHelperText: ''
  };

  const [showDialogLoader, setShowDialogLoader] = useState(false);
  const [userPasswordText, updateUserPasswordText] = useState('');
  const [formValues, updateFormValues] = useState<any>(formDefaultValues);
  const [formErrorState, updateFormError] = useState(formErrorDefaultValues);
  const [showActivationConfirmation, setShowActivationConfirmation] = useState(false);
  const [displayCopyPasswordOption, enableDisplayCopyPasswordOption] = useState(false);
  const [showResetPasswordConfirmation, setShowResetPasswordConfirmation] = useState(false);
  const [showResetMfaConfirmation, setShowResetMfaConfirmation] = useState(false);
  const [showResendInviteConfirmation, setShowResendInviteConfirmation] = useState(false);
  const [mfaActive, setMFAActived] = useState(false);
  const userIdp = selectGetAllIdps.find((idp: { id: string }) => idp.id === selectedUserDetails.idpId);
  const isNativeUser = userIdp?.idpType === NATIVE_IDP_TYPE || userIdp?.type === 'NATIVE';
  const defaultSourceIdpId = userIdp?.idpSchema?.externalIdentityProviders?.find((idp: any) => idp.default);

  const handleCloseCopyPasswordOption = () => {
    enableDisplayCopyPasswordOption(false);
  };

  const displaySnackbar = (message: string, severity: 'success' | 'error') => {
    dispatch(
      setSnackBarMessage({
        showSnackbar: true,
        autoHideDuration: 5000,
        message: message,
        severity: severity
      })
    );
  };

  const snackbarSetHidden = useCallback(() => {
    dispatch(hideSnackbar());
  }, [dispatch]);

  const updateFormFields = (fieldName: string, value: any) => {
    updateFormValues({ ...formValues, [fieldName]: value });
    updateFormError(formErrorDefaultValues);
    dispatch(updateUserFormDataField(fieldName, { value, error: false, errorHelperText: '' }))
  };

  const idpTypeChangeHandler = (_e: any) => { };

  const getSelectedItem = (value: string, key: string, itemList: any) => {
    const selItem = itemList.filter((item: any) => {
      return item[key] === value;
    });
    return Array.isArray(selItem) && selItem.length > 0 ? selItem[0] : '';
  };
  useEffect(() => {
    defaultSourceIdpId && getIdpById(defaultSourceIdpId.default).then((response: any) => {
      const idp = response;
      setMFAActived(idp.mfaActive);
    }).catch((error: any) => { console.log(error); });
  }, [defaultSourceIdpId]);

  useEffect(() => {
    snackbarSetHidden();
    Object.keys(selectedUserDetails).forEach((field: any) => {
      updateFormFields(field, selectedUserDetails[field]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedUserDetails, snackbarSetHidden]);

  const getFormFieldErrorStatus = (fieldName: string) => {
    return userFormValues[fieldName] && userFormValues[fieldName].error ? true : false;
  };

  const updateIsAccountEnabledFlag = (enabled: boolean) => {
    const request = isNativeUser ? signInBlockEnable(selectedUserDetails.id, { enabled }, appLocation?.name) :
      updateNativeUserDetails(id, { isAccountEnabled: enabled });
    request.then(() => {
      setShowDialogLoader(false);
      displaySnackbar(
        enabled
          ? interpolateString([userName], MESSAGES.ALLOW_SIGNIN_SUCCESS)
          : interpolateString([userName], MESSAGES.BLOCK_SIGNIN_SUCCESS),
        'success'
      );
      getUserDetailsById(dispatch, id, isNativeUser, appLocation?.name);
      setShowActivationConfirmation(false);
    }).catch((_error: any) => {
      setShowDialogLoader(false);
      displaySnackbar(
        enabled
          ? interpolateString([userName], MESSAGES.ALLOW_SIGNIN_ERROR)
          : interpolateString([userName], MESSAGES.BLOCK_SIGNIN_ERROR),
        'error'
      );
      setShowActivationConfirmation(false);
    });
  };

  const getPasswordCopyDialog = () => {
    return (
      displayCopyPasswordOption && (
        <LuiModal
          isOpen={displayCopyPasswordOption}
          handleClose={() => {
            handleCloseCopyPasswordOption();
          }}
          data-testid={'copy-password-dialog'}
          modalSize="sm"
          modalTitle={LABELS.TEMPORARY_PASSWORD}
          primaryAction={
            <Button
              variant="contained"
              data-testid={'copy-password-dialog-button-close'}
              name="copyPasswordCloseBtn"
              id="copyPasswordCloseBtn"
              color="primary"
              disableElevation
              onClick={() => {
                handleCloseCopyPasswordOption();
              }}
            >
              {LABELS.BUTTON_CLOSE}
            </Button>
          }
        >
          {<UserPasswordDisplay passwordText={userPasswordText} userName={userName} />}
        </LuiModal>
      )
    );
  };

  const getFormFieldValue = createFormValueGetter(userFormValues);

  const getFormFieldErrorHelperText = (fieldName: string) => {
    return userFormValues[fieldName] && userFormValues[fieldName].errorHelperText
      ? userFormValues[fieldName].errorHelperText
      : '';
  };

  const resetPassword = () => {
    snackbarSetHidden();
    resetUserPassword(id, appLocation?.name).then(response => {
      if (response) {
        setShowDialogLoader(false);
        setShowResetPasswordConfirmation(false);
        displaySnackbar(interpolateString([userName], MESSAGES.PASSWORD_RESET_SUCCESS), 'success');
        updateUserPasswordText(response.password);
        enableDisplayCopyPasswordOption(true);
      }
    }).catch((_error: any) => {
      setShowDialogLoader(false);
      displaySnackbar(interpolateString([userName], MESSAGES.PASSWORD_RESET_FAILURE), 'error');
      setShowResetPasswordConfirmation(false);
    });
  };

  const resetMFA = () => {
    snackbarSetHidden();
    resetUserMFA(id).then(() => {
      setShowDialogLoader(false);
      setShowResetMfaConfirmation(false);
      displaySnackbar(interpolateString([userName], MESSAGES.MFA_RESET_SUCCESS), 'success');
    }).catch((_error: any) => {
      const errorMessage = _error.response.data.ErrorMessage || _error.message
      setShowDialogLoader(false);
      displaySnackbar(`${MESSAGES.MFA_RESET_FAILURE} ${errorMessage}`, 'error');
      setShowResetMfaConfirmation(false);
    });
  };

  const getUserSignInDetails = () => (
    <>
      <Grid item xs={12}>
        <Box className={classes.divider}>
          <Divider />
        </Box>
      </Grid>
      <Grid xs={12}>
        <Box className={classes.tabHeader}>{LABELS.SIGN_IN_DETAILS}</Box>
      </Grid>
      <Grid item xs={3}>
        <Box className={classes.contentContainer}>
          <TextField
            required
            fullWidth
            id="userName"
            variant="standard"
            name="userName"
            disabled={true}
            label={LABELS.USER_NAME}
            data-testid={"user-name"}
            className={classes.textInputSignIn}
            inputProps={{ maxLength: 100 }}
            value={userFormValues.userName.value}
            defaultValue={selectedUserDetails.userName}
          />
        </Box>
      </Grid>
    </>
  );

  const getUserContactDetails = () => (
    <>
      <Grid item xs={12}>
        <Box className={classes.divider}>
          <Divider />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box className={classes.tabHeader}>{LABELS.CONTACT_DETAILS}</Box>
      </Grid>
      <Grid container spacing={4}>
        <Grid item xs={3}>
          <Box className={classes.contentContainer}>
            <TextField
              {...getTextFieldHelperProps({
                required: !isNativeUser,
                errorHelperText: getFormFieldErrorHelperText(USER_FORM_FIELDS.EMAIL),
                isNativeUser
              })}
              fullWidth
              id="email"
              variant="standard"
              name="email"
              data-testid="email"
              label={LABELS.EMAIL}
              className={classes.textInput}
              inputProps={{ maxLength: 100 }}
              value={userFormValues.email.value}
              defaultValue={selectedUserDetails.email}
              onChange={(e: any) => updateFormFields('email', e.target.value)}
            />
          </Box>
        </Grid>
        {!isNativeUser && (
          <Grid item xs={3}>
            <Box className={classes.contentContainer}>
              <Box className={classes.modalFormContent}>
                <PhoneInput
                  international
                  limitMaxLength
                  placeholder={LABELS.MOBILE_PHONE}
                  id={USER_FORM_FIELDS.MOBILE_PHONE}
                  data-testid={USER_FORM_FIELDS.MOBILE_PHONE}
                  value={getFormFieldValue(USER_FORM_FIELDS.MOBILE_PHONE) as Value}
                  className={`${classes.modalTextBoxPhone} ${getFormFieldErrorStatus(USER_FORM_FIELDS.MOBILE_PHONE) ? classes.modalTextBoxPhoneError : ''}`}
                  countrySelectProps={{
                    unicodeFlags: false,
                    arrowComponent: () => {
                      return <ArrowTrendDownFill className={classes.countrySelectArrow} />;
                    }
                  }}
                  onChange={value => {
                    dispatch(
                      updateUserFormDataField(USER_FORM_FIELDS.MOBILE_PHONE, {
                        value: value || '',
                        error: false,
                        errorHelperText: ''
                      })
                    );
                  }}
                />
              </Box>
              <FormHelperText className={classes.mobilePhoneError}
                id={`${USER_FORM_FIELDS.MOBILE_PHONE}-error`}
                error={getFormFieldErrorStatus(USER_FORM_FIELDS.MOBILE_PHONE)}
              >
                {getFormFieldErrorHelperText(USER_FORM_FIELDS.MOBILE_PHONE)}
              </FormHelperText>
            </Box>
          </Grid>
        )}
      </Grid>
    </>
  );
  const resendInviteLink = (userId: string) => {
    resendInvite(userId).then(() => {
      setShowDialogLoader(false);
      setShowResendInviteConfirmation(false);
      displaySnackbar(interpolateString([userName], MESSAGES.RESEND_INVITE_SUCCESS), 'success');
    }).catch((_error: any) => {
      const errorMessage = _error.response.data.message || _error.message;
      setShowDialogLoader(false);
      displaySnackbar(`${MESSAGES.RESEND_INVITE_FAILURE} ${errorMessage}`, 'error');
      setShowResendInviteConfirmation(false);
    });
  }
  return (

    <Box className={classes.nativeUserContainer}>
      <Box className={classes.nativeUserTabHeaderWrapper}>
        <Box className={classes.tabHeader}>{LABELS.GENERAL}</Box>
        <Box className={classes.buttonWrapper}>
          <Button
            disableElevation
            color="primary"
            variant="outlined"
            name="activation-button"
            id="activation-button"
            data-testid="activation-button"
            className={classes.rightToolBarButton}
            onClick={() => setShowActivationConfirmation(true)}
          >
            {selectedUserDetails.isAccountEnabled ? LABELS.BUTTON_BLOCK_SIGN_IN : LABELS.BUTTON_ALLOW_SIGN_IN}
          </Button>
          {!isNativeUser && <Button
            disableElevation
            color="primary"
            variant="outlined"
            name="resetPasswordButton"
            id="resetPasswordButton"
            data-testid="reset-password-button"
            className={classes.toolBarButton}
            onClick={() => {
              setShowResetPasswordConfirmation(true);
            }}
          >
            {LABELS.BUTTON_RESET_PASSWORD}
          </Button>}
          {mfaActive && isNativeUser && selectedUserDetails.isMfaEnabled && <Button
            variant="outlined"
            name="resetMfaButton"
            id="resetMfaButton"
            color="primary"
            data-testid="reset-mfa-button"
            disableElevation
            className={classes.toolBarButton}
            onClick={() => {
              setShowResetMfaConfirmation(true);
            }}
          >
            {LABELS.BUTTON_RESET_MFA}
          </Button>}
          {isNativeUser && selectedUserDetails.preRegistration && !selectedUserDetails.registrationCompleted && <Button
            variant="outlined"
            name="resendInviteButton"
            id="resendInviteButton"
            color="primary"
            data-testid="resend-invite-button"
            disableElevation
            className={classes.toolBarButton}
            onClick={() => {
              setShowResendInviteConfirmation(true);
            }}
          >
            {LABELS.BUTTON_RESEND_INVITE}
          </Button>}
          {(showResetPasswordConfirmation || showResetMfaConfirmation) && (
            <AlertDialog
              data-testid={`reset-${showResetPasswordConfirmation ? 'password' : 'mfa'}-alert`}
              dialogTitle={showResetPasswordConfirmation ? interpolateString([userName], MESSAGES.RESET_PASSWORD_CONFIRMATION) : interpolateString([userName], MESSAGES.RESET_MFA_CONFIRMATION)}
              dialogContentText={showResetPasswordConfirmation ? LABELS.RESET_PASSWORD_TITLE : LABELS.RESET_MFA_TITLE}
              recordName={userName}
              primaryButtonText={showResetPasswordConfirmation ? LABELS.BUTTON_RESET_PASSWORD : LABELS.BUTTON_RESET_MFA}
              secondButtonText={LABELS.BUTTON_CANCEL}
              handleOnCancel={() => {
                if (showResetPasswordConfirmation) {
                  setShowResetPasswordConfirmation(false);
                  setShowDialogLoader(false);
                } else {
                  setShowResetMfaConfirmation(false);
                  setShowDialogLoader(false);
                }
              }}
              handleOnOk={() => {
                setShowDialogLoader(true);
                showResetPasswordConfirmation ? resetPassword() : resetMFA()
              }}
              showDialogLoader={showDialogLoader}
              hideCanNotBeUndoneMessage={true}
            />
          )}
          {isNativeUser && <ResetPassword
            userName={userName}
            userIdp={userIdp}
            userId={id}
          />}
          {getPasswordCopyDialog()}
          {showActivationConfirmation && (
            <AlertDialog
              data-testid="activation-alert"
              dialogContentText={
                selectedUserDetails.isAccountEnabled ? LABELS.BLOCK_SIGN_IN_TITLE : LABELS.ALLOW_SIGN_IN_TITLE
              }
              dialogTitle={
                selectedUserDetails.isAccountEnabled
                  ? interpolateString([userName], MESSAGES.BLOCK_SIGN_IN_CONFIRMATION)
                  : interpolateString([userName], MESSAGES.ALLOW_SIGN_IN_CONFIRMATION)
              }
              recordName={''}
              primaryButtonText={
                selectedUserDetails.isAccountEnabled ? LABELS.BUTTON_BLOCK_SIGN_IN : LABELS.BUTTON_ALLOW_SIGN_IN
              }
              secondButtonText={LABELS.BUTTON_CANCEL}
              handleOnCancel={() => {
                setShowActivationConfirmation(false);
                setShowDialogLoader(false);
              }}
              handleOnOk={() => {
                setShowDialogLoader(true);
                updateIsAccountEnabledFlag(selectedUserDetails.isAccountEnabled ? false : true);
              }}
              showDialogLoader={showDialogLoader}
              hideCanNotBeUndoneMessage={true}
              primaryButtonColor={!selectedUserDetails.isAccountEnabled ? 'primary' : 'error'}
            />
          )}
          {showResendInviteConfirmation && (
            <AlertDialog
              data-testid="resend-invite-alert"
              dialogContentText={LABELS.BUTTON_RESEND_INVITE}
              dialogTitle={interpolateString([userName], MESSAGES.RESEND_INVITE_CONFIRMATION)}
              recordName={''}
              primaryButtonText={LABELS.BUTTON_RESEND_INVITE}
              secondButtonText={LABELS.BUTTON_CANCEL}
              handleOnCancel={() => {
                setShowResendInviteConfirmation(false);
                setShowDialogLoader(false);
              }}
              handleOnOk={() => {
                setShowDialogLoader(true);
                resendInviteLink(selectedUserDetails.id);
              }}
              showDialogLoader={showDialogLoader}
              hideCanNotBeUndoneMessage={true}
              primaryButtonColor={'primary'}
            />
          )}
        </Box>
      </Box>
      <Box>
        <Grid container spacing={4} className={classes.gridViewWrap}>
          <Grid item xs={4}>
            <Box className={classes.contentContainer}>
              <LuiSelect
                required
                disabled
                width={'100%'}
                maxHeight={350}
                id={USER_FORM_FIELDS.IDP_ID}
                data-testid={USER_FORM_FIELDS.IDP_ID}
                inputLabel={LABELS.IDENTITY_PROVIDER}
                onSelectionChange={idpTypeChangeHandler}
                itemData={idpList}
                selectedItem={getSelectedItem(selectedUserDetails.idpId, KEY_NAMES.ID, idpList)}
              />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box className={classes.contentContainer}>
              <TextField
                {...getTextFieldHelperProps({
                  errorHelperText: getFormFieldErrorHelperText(USER_FORM_FIELDS.GIVEN_NAME),
                  isNativeUser
                })}
                fullWidth
                variant="standard"
                id="firstName"
                name="firstName"
                data-testid="first-name"
                label={LABELS.FIRST_NAME}
                className={classes.textInput}
                inputProps={{ maxLength: 100 }}
                value={userFormValues.givenName.value}
                defaultValue={selectedUserDetails.givenName}
                onChange={(e: any) => updateFormFields('givenName', e.target.value)}
              />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box className={classes.contentContainer}>
              <TextField
                {...getTextFieldHelperProps({
                  errorHelperText: getFormFieldErrorHelperText(USER_FORM_FIELDS.LAST_NAME),
                  isNativeUser
                })}
                fullWidth
                id="lastName"
                name="lastName"
                data-testid="last-name"
                label={LABELS.LAST_NAME}
                variant="standard"
                className={classes.textInput}
                inputProps={{ maxLength: 100 }}
                value={userFormValues.lastName.value}
                defaultValue={selectedUserDetails.lastName}
                onChange={(e: any) => updateFormFields('lastName', e.target.value)}
              />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box className={classes.contentContainer}>
              <TextField
                {...getTextFieldHelperProps({
                  required: !isNativeUser,
                  errorHelperText: getFormFieldErrorHelperText(USER_FORM_FIELDS.DISPLAY_NAME),
                  isNativeUser
                })}
                fullWidth
                id="displayName"
                name="displayName"
                variant="standard"
                data-testid="display-name"
                label={LABELS.DISPLAY_NAME}
                className={classes.textInput}
                inputProps={{ maxLength: 100 }}
                value={userFormValues.displayName.value}
                defaultValue={selectedUserDetails.displayName}
                onChange={(e: any) => updateFormFields('displayName', e.target.value)}
              />
            </Box>
          </Grid>
          <Grid item xs={8}>
            <Box className={classes.contentContainer}>
              <DisplayContent
                testId={'idpSubjectId'}
                contentText={LABELS.SUBJECT_ID}
                handleCopyClick={handleCopyClick}
                content={selectedUserDetails.idpSubjectId}
                isNewUser={!selectedUserDetails.idpSubjectId || selectedUserDetails.idpSubjectId === '' ? true : false}
              />
            </Box>
          </Grid>
        </Grid>
        {isNativeUser ? (
          <>
            {getUserContactDetails()}
            {getUserSignInDetails()}
          </>
        ) : (
          <>
            {getUserSignInDetails()}
            {getUserContactDetails()}
          </>
        )}
        {!isNativeUser && (
          <>
            <Grid item xs={12}>
              <Box className={classes.divider}>
                <Divider />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box className={classes.tabHeader}>{LABELS.SETTINGS}</Box>
            </Grid>
            <Grid container spacing={4}>

              <Grid item xs={3}>
                <Box className={classes.mfaWrap}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        id={USER_FORM_FIELDS.IS_MFA_ENABLED}
                        name={USER_FORM_FIELDS.IS_MFA_ENABLED}
                        data-testid={USER_FORM_FIELDS.IS_MFA_ENABLED}
                        checked={getFormFieldValue(USER_FORM_FIELDS.IS_MFA_ENABLED) ? true : false}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          dispatch(
                            updateUserFormDataField(USER_FORM_FIELDS.IS_MFA_ENABLED, {
                              value: event.target.checked,
                              error: false,
                              errorHelperText: ''
                            })
                          );
                          dispatch(
                            updateUserFormDataField(USER_FORM_FIELDS.PREFERRED_MFA, {
                              value: '',
                              error: false,
                              errorHelperText: ''
                            })
                          );
                          dispatch(
                            updateUserFormDataField(USER_FORM_FIELDS.MOBILE_PHONE, {
                              ...userFormValues[USER_FORM_FIELDS.MOBILE_PHONE],
                              error: false,
                              errorHelperText: ''
                            })
                          );
                        }}
                      />
                    }
                    label={LABELS.ENABLE_MFA}
                  />
                </Box>
              </Grid>
              <Grid item xs={3}>
                {getFormFieldValue(USER_FORM_FIELDS.IS_MFA_ENABLED) && (
                  <>
                    <FormControl error={getFormFieldErrorStatus(USER_FORM_FIELDS.PREFERRED_MFA)}>
                      <Box className={classes.dropDownText}>
                        <LuiSelect
                          onSelectionChange={e => {
                            dispatch(
                              updateUserFormDataField(USER_FORM_FIELDS.PREFERRED_MFA, {
                                value: e.target.value.key,
                                error: false,
                                errorHelperText: ''
                              })
                            );

                            if (e.target.value.key === KEY_NAMES.PREFERRED_MFA_EMAIL) {
                              dispatch(
                                updateUserFormDataField(USER_FORM_FIELDS.MOBILE_PHONE, {
                                  ...userFormValues[USER_FORM_FIELDS.MOBILE_PHONE],
                                  error: false,
                                  errorHelperText: ''
                                })
                              );
                            }
                          }}
                          itemData={PREFERRED_MFA_LIST}
                          id={USER_FORM_FIELDS.PREFERRED_MFA}
                          data-testid={USER_FORM_FIELDS.PREFERRED_MFA}
                          inputLabel={LABELS.PREFERRED_MFA}
                          selectedItem={getSelectedItem(
                            getFormFieldValue(USER_FORM_FIELDS.PREFERRED_MFA) as string,
                            'key',
                            PREFERRED_MFA_LIST
                          )}
                          disabled={false}
                          width={400}
                          maxHeight={350}
                          error={getFormFieldErrorStatus(USER_FORM_FIELDS.PREFERRED_MFA)}
                        />
                      </Box>
                    </FormControl>
                    <FormHelperText
                      id={`${USER_FORM_FIELDS.PREFERRED_MFA}-error`}
                      error={getFormFieldErrorStatus(USER_FORM_FIELDS.PREFERRED_MFA)}
                    >
                      {getFormFieldErrorHelperText(USER_FORM_FIELDS.PREFERRED_MFA)}
                    </FormHelperText>
                  </>
                )}</Grid>

            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
};

export default NativeUserDetails;
